import React, { useState } from 'react';
import { Select } from '../ui/Select';
import { Plus, X, Eye, GripVertical } from 'lucide-react';
import { Modal } from '../ui/Modal';
interface Transaction {
  id: string;
  channel: string;
  category: 'disbursement' | 'repayment';
  feeProfile: string;
  taxProfile: string;
  glAccount: string;
  priority: number;
}
export function TransactionsTab() {
  const [selectedChannel, setSelectedChannel] = useState('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewModalContent, setViewModalContent] = useState<{
    type: 'fee' | 'tax';
    data: any;
  } | null>(null);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const channelOptions = [{
    value: '',
    label: 'Select a channel...',
    disabled: true
  }, {
    value: 'disbursement_header',
    label: '--- Loan Disbursement Channels ---',
    disabled: true
  }, {
    value: 'credit_card',
    label: 'Credit Card'
  }, {
    value: 'raast',
    label: 'RAAST'
  }, {
    value: 'ibft',
    label: 'IBFT'
  }, {
    value: 'repayment_header',
    label: '--- Loan Repayment Channels ---',
    disabled: true
  }, {
    value: '1bill',
    label: '1Bill'
  }, {
    value: 'pos_settlement',
    label: 'POS Settlement'
  }];
  const feeProfileOptions = [{
    value: '',
    label: 'Select fee profile...'
  }, {
    value: 'processing',
    label: 'Processing Fee'
  }, {
    value: 'origination',
    label: 'Origination Fee'
  }, {
    value: 'annual',
    label: 'Annual Fee'
  }, {
    value: 'document',
    label: 'Document Fee'
  }];
  const taxProfileOptions = [{
    value: '',
    label: 'Select tax profile...'
  }, {
    value: 'vat',
    label: 'VAT'
  }, {
    value: 'gst',
    label: 'GST'
  }, {
    value: 'service_tax',
    label: 'Service Tax'
  }, {
    value: 'withholding',
    label: 'Withholding Tax'
  }];
  const glAccountOptions = [{
    value: '',
    label: 'Select GL account...'
  }, {
    value: 'gl_1000',
    label: 'GL-1000 - Loan Receivables'
  }, {
    value: 'gl_2000',
    label: 'GL-2000 - Interest Income'
  }, {
    value: 'gl_3000',
    label: 'GL-3000 - Fee Income'
  }, {
    value: 'gl_4000',
    label: 'GL-4000 - Penalty Income'
  }];
  // Mock fee details
  const feeDetails: Record<string, any> = {
    processing: {
      name: 'Processing Fee',
      code: 'FEE-PROC-001',
      type: 'Percentage',
      value: '2.5%',
      glAccount: 'GL-4200 - Processing Fee Income',
      status: 'Active',
      effectiveDate: '2024-01-01'
    },
    origination: {
      name: 'Origination Fee',
      code: 'FEE-ORIG-001',
      type: 'Fixed',
      value: 'PKR 500',
      glAccount: 'GL-4100 - Fee Income',
      status: 'Active',
      effectiveDate: '2024-01-01'
    },
    annual: {
      name: 'Annual Fee',
      code: 'FEE-ANN-001',
      type: 'Fixed',
      value: 'PKR 1000',
      glAccount: 'GL-4300 - Service Charges',
      status: 'Active',
      effectiveDate: '2024-01-01'
    },
    document: {
      name: 'Document Fee',
      code: 'FEE-DOC-001',
      type: 'Fixed',
      value: 'PKR 200',
      glAccount: 'GL-4100 - Fee Income',
      status: 'Active',
      effectiveDate: '2024-01-01'
    }
  };
  // Mock tax details
  const taxDetails: Record<string, any> = {
    vat: {
      name: 'Value Added Tax',
      code: 'TAX-VAT-001',
      type: 'Percentage',
      rate: '18%',
      glAccount: 'GL-2300 - Tax Payable - VAT',
      status: 'Active',
      effectiveDate: '2024-01-01'
    },
    gst: {
      name: 'Goods and Services Tax',
      code: 'TAX-GST-001',
      type: 'Percentage',
      rate: '15%',
      glAccount: 'GL-2100 - Tax Payable',
      status: 'Active',
      effectiveDate: '2024-01-01'
    },
    service_tax: {
      name: 'Service Tax',
      code: 'TAX-SVC-001',
      type: 'Percentage',
      rate: '16%',
      glAccount: 'GL-2100 - Tax Payable - Service Tax',
      status: 'Active',
      effectiveDate: '2024-01-01'
    },
    withholding: {
      name: 'Withholding Tax',
      code: 'TAX-WHT-001',
      type: 'Percentage',
      rate: '10%',
      glAccount: 'GL-2400 - Tax Payable - Withholding',
      status: 'Active',
      effectiveDate: '2024-01-01'
    }
  };
  const handleViewFee = (feeProfile: string) => {
    if (feeProfile && feeDetails[feeProfile]) {
      setViewModalContent({
        type: 'fee',
        data: feeDetails[feeProfile]
      });
      setViewModalOpen(true);
    }
  };
  const handleViewTax = (taxProfile: string) => {
    if (taxProfile && taxDetails[taxProfile]) {
      setViewModalContent({
        type: 'tax',
        data: taxDetails[taxProfile]
      });
      setViewModalOpen(true);
    }
  };
  const addTransaction = () => {
    if (!selectedChannel || selectedChannel.includes('_header')) return;
    const isDisbursement = ['credit_card', 'raast', 'ibft'].includes(selectedChannel);
    const category = isDisbursement ? 'disbursement' : 'repayment';
    // Get the next priority for this category
    const categoryTransactions = transactions.filter(t => t.category === category);
    const nextPriority = categoryTransactions.length + 1;
    const newTransaction: Transaction = {
      id: Date.now().toString(),
      channel: selectedChannel,
      category,
      feeProfile: '',
      taxProfile: '',
      glAccount: '',
      priority: nextPriority
    };
    setTransactions([...transactions, newTransaction]);
    setSelectedChannel('');
  };
  const removeTransaction = (id: string) => {
    const removedTransaction = transactions.find(t => t.id === id);
    if (!removedTransaction) return;
    const updatedTransactions = transactions.filter(t => t.id !== id).map(t => {
      // Adjust priorities for transactions in the same category
      if (t.category === removedTransaction.category && t.priority > removedTransaction.priority) {
        return {
          ...t,
          priority: t.priority - 1
        };
      }
      return t;
    });
    setTransactions(updatedTransactions);
  };
  const updateTransaction = (id: string, field: 'feeProfile' | 'taxProfile' | 'glAccount', value: string) => {
    setTransactions(transactions.map(t => t.id === id ? {
      ...t,
      [field]: value
    } : t));
  };
  const getChannelLabel = (value: string) => {
    const option = channelOptions.find(o => o.value === value);
    return option?.label || value;
  };
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedItem(id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) return;
    const draggedTransaction = transactions.find(t => t.id === draggedItem);
    const targetTransaction = transactions.find(t => t.id === targetId);
    if (!draggedTransaction || !targetTransaction) return;
    if (draggedTransaction.category !== targetTransaction.category) return;
    const updatedTransactions = [...transactions];
    const draggedIndex = updatedTransactions.findIndex(t => t.id === draggedItem);
    const targetIndex = updatedTransactions.findIndex(t => t.id === targetId);
    // Swap priorities
    const tempPriority = updatedTransactions[draggedIndex].priority;
    updatedTransactions[draggedIndex].priority = updatedTransactions[targetIndex].priority;
    updatedTransactions[targetIndex].priority = tempPriority;
    setTransactions(updatedTransactions);
  };
  const handleDragEnd = () => {
    setDraggedItem(null);
  };
  const disbursementTransactions = transactions.filter(t => t.category === 'disbursement').sort((a, b) => a.priority - b.priority);
  const repaymentTransactions = transactions.filter(t => t.category === 'repayment').sort((a, b) => a.priority - b.priority);
  const renderTransactionTable = (categoryTransactions: Transaction[], title: string) => {
    if (categoryTransactions.length === 0) return null;
    return <div className="mb-6" data-id="element-928">
        <h3 className="text-sm font-semibold text-gray-700 mb-3" data-id="element-929">{title}</h3>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-930">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-931">
            <thead className="bg-gray-50" data-id="element-932">
              <tr data-id="element-933">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12" data-id="element-934">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-935">
                  Channel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-936">
                  Fee Profile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-937">
                  Tax Profile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-938">
                  GL Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-939">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-940">
              {categoryTransactions.map(transaction => <tr key={transaction.id} draggable onDragStart={e => handleDragStart(e, transaction.id)} onDragOver={e => handleDragOver(e, transaction.id)} onDragEnd={handleDragEnd} className={`hover:bg-gray-50 cursor-move ${draggedItem === transaction.id ? 'opacity-50' : ''}`} data-id="element-941">
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-942">
                    <div className="flex items-center gap-2" data-id="element-943">
                      <GripVertical className="h-4 w-4 text-gray-400" data-id="element-944" />
                      <span className="text-sm font-semibold text-gray-900" data-id="element-945">
                        {transaction.priority}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-946">
                    {getChannelLabel(transaction.channel)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-947">
                    <div className="flex items-center gap-2" data-id="element-948">
                      <div className="flex-1" data-id="element-949">
                        <Select value={transaction.feeProfile} onChange={e => updateTransaction(transaction.id, 'feeProfile', e.target.value)} options={feeProfileOptions} data-id="element-950" />
                      </div>
                      {transaction.feeProfile && <button onClick={() => handleViewFee(transaction.feeProfile)} className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors" title="View fee details" data-id="element-951">
                          <Eye className="h-4 w-4" data-id="element-952" />
                        </button>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-953">
                    <div className="flex items-center gap-2" data-id="element-954">
                      <div className="flex-1" data-id="element-955">
                        <Select value={transaction.taxProfile} onChange={e => updateTransaction(transaction.id, 'taxProfile', e.target.value)} options={taxProfileOptions} data-id="element-956" />
                      </div>
                      {transaction.taxProfile && <button onClick={() => handleViewTax(transaction.taxProfile)} className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors" title="View tax details" data-id="element-957">
                          <Eye className="h-4 w-4" data-id="element-958" />
                        </button>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-959">
                    <Select value={transaction.glAccount} onChange={e => updateTransaction(transaction.id, 'glAccount', e.target.value)} options={glAccountOptions} data-id="element-960" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-961">
                    <button onClick={() => removeTransaction(transaction.id)} className="text-red-600 hover:text-red-800" data-id="element-962">
                      <X className="h-5 w-5" data-id="element-963" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>;
  };
  return <div className="space-y-6 max-w-6xl animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-964">
      <div className="flex gap-3" data-id="element-965">
        <div className="flex-1" data-id="element-966">
          <Select label="Transaction Channels" value={selectedChannel} onChange={e => setSelectedChannel(e.target.value)} options={channelOptions} data-id="element-967" />
        </div>
        <div className="flex items-end" data-id="element-968">
          <button onClick={addTransaction} disabled={!selectedChannel || selectedChannel.includes('_header')} className="h-10 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center" data-id="element-969">
            <Plus className="h-4 w-4 mr-2" data-id="element-970" />
            Add Channel
          </button>
        </div>
      </div>

      {transactions.length > 0 && <div data-id="element-971">
          {renderTransactionTable(disbursementTransactions, 'Loan Disbursement Channels')}
          {renderTransactionTable(repaymentTransactions, 'Loan Repayment Channels')}
        </div>}

      {transactions.length === 0 && <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-972">
          <p className="text-gray-500" data-id="element-973">
            No transaction channels added yet. Select a channel above to get
            started.
          </p>
        </div>}

      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200" data-id="element-974">
        <h4 className="text-sm font-semibold text-blue-800 mb-2" data-id="element-975">
          Priority Information
        </h4>
        <p className="text-sm text-blue-600" data-id="element-976">
          Drag and drop rows to reorder channel priorities. Priority 1 will be
          attempted first, followed by Priority 2, and so on.
        </p>
      </div>

      {/* View Details Modal */}
      {viewModalContent && <Modal isOpen={viewModalOpen} onClose={() => setViewModalOpen(false)} title={`${viewModalContent.type === 'fee' ? 'Fee' : 'Tax'} Profile Details`} data-id="element-977">
          <div className="space-y-4" data-id="element-978">
            <div className="grid grid-cols-2 gap-4" data-id="element-979">
              <div data-id="element-980">
                <label className="text-sm font-medium text-gray-700" data-id="element-981">
                  Name
                </label>
                <p className="text-sm text-gray-900 mt-1" data-id="element-982">
                  {viewModalContent.data.name}
                </p>
              </div>
              <div data-id="element-983">
                <label className="text-sm font-medium text-gray-700" data-id="element-984">
                  Code
                </label>
                <p className="text-sm text-gray-900 mt-1" data-id="element-985">
                  {viewModalContent.data.code}
                </p>
              </div>
              <div data-id="element-986">
                <label className="text-sm font-medium text-gray-700" data-id="element-987">
                  Type
                </label>
                <p className="text-sm text-gray-900 mt-1" data-id="element-988">
                  {viewModalContent.data.type}
                </p>
              </div>
              <div data-id="element-989">
                <label className="text-sm font-medium text-gray-700" data-id="element-990">
                  {viewModalContent.type === 'fee' ? 'Value' : 'Rate'}
                </label>
                <p className="text-sm text-gray-900 mt-1" data-id="element-991">
                  {viewModalContent.type === 'fee' ? viewModalContent.data.value : viewModalContent.data.rate}
                </p>
              </div>
              <div data-id="element-992">
                <label className="text-sm font-medium text-gray-700" data-id="element-993">
                  GL Account
                </label>
                <p className="text-sm text-gray-900 mt-1" data-id="element-994">
                  {viewModalContent.data.glAccount}
                </p>
              </div>
              <div data-id="element-995">
                <label className="text-sm font-medium text-gray-700" data-id="element-996">
                  Status
                </label>
                <p className="text-sm text-gray-900 mt-1" data-id="element-997">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-998">
                    {viewModalContent.data.status}
                  </span>
                </p>
              </div>
              <div className="col-span-2" data-id="element-999">
                <label className="text-sm font-medium text-gray-700" data-id="element-1000">
                  Effective Date
                </label>
                <p className="text-sm text-gray-900 mt-1" data-id="element-1001">
                  {viewModalContent.data.effectiveDate}
                </p>
              </div>
            </div>
          </div>
        </Modal>}
    </div>;
}